<template>
  <v-app>
    <!-- Staging environment banner -->
    <v-system-bar color="warning" v-if="isStaging" app>
      <v-spacer />
      <v-icon size="small" class="mr-2">mdi-alert</v-icon>
      <span class="font-weight-bold">STAGING ENVIRONMENT</span>
      <span class="mx-2">|</span>
      <span>Changes here do not affect production</span>
      <v-spacer />
    </v-system-bar>

    <!-- drawer reserves its width automatically -->
    <TheNavigationDrawer app :expanded="expanded" />

    <!-- app-bar reserves its height -->
    <TheAppBar app @menu-btn-click="toggleDrawer()" />

    <!-- only this region grows and scrolls -->
  <v-main @scroll="handleScroll" class="main-content">
      <RouterView v-slot="{ Component }">
        <div class="router-view-wrapper">
          <component :is="Component" />
        </div>
      </RouterView>
    </v-main>

    <ToastMessages />

    <!-- footer fixed at bottom -->
    <v-footer app class="d-flex align-end justify-end">
      <div class="text-caption ma-2 pa-2 mt-auto">
        <v-icon>mdi-copyright</v-icon>
        {{ new Date().getFullYear() }} | <strong>Razayya Technologies, Inc.</strong>. All Rights Reserved
      </div>
    </v-footer>
  </v-app>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore, useDashboardConfigStore, useJobStore } from '@/stores'
import TheAppBar from './components/TheAppBar.vue'
import TheNavigationDrawer from './components/TheNavigationDrawer.vue'
import ToastMessages from './components/ToastMessages.vue'

const isStaging = process.env.VUE_APP_ENVIRONMENT === 'staging'

const router = useRouter()
const route = useRoute()
const { smAndDown } = useDisplay()
const expanded = ref(!smAndDown.value)

function toggleDrawer() {
  expanded.value = !expanded.value
}

const authStore = useAuthStore()
const dashboardConfigStore = useDashboardConfigStore()
const jobStore = useJobStore()
const { getLastModule } = authStore

// Gracefully close SSE connection before page unload to prevent server-side cancellation exceptions
const handleBeforeUnload = () => {
  jobStore.disconnectSSE()
}

onMounted(async () => {
  window.addEventListener('beforeunload', handleBeforeUnload)

  if (authStore.isLoggedIn) {
    await dashboardConfigStore.ensureLoaded()
    await jobStore.initializeSSE()
  }

  const windowPath = window.location.pathname
  const routerPath = router.currentRoute.value.path

  // Don't redirect if on a public route (e.g., shared reports)
  const isPublicRoute = route.meta.requiresAuth === false

  if (windowPath === routerPath && !isPublicRoute) {
    const lastModule = getLastModule()
    if (lastModule) {
      router.push(lastModule).catch(err => {
        console.error('Failed to navigate to last module:', err)
      })
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

// Watch for login/logout events
watch(() => authStore.isLoggedIn, async (newValue, oldValue) => {
  // If user just logged in
  if (oldValue === false && newValue === true) {
    await jobStore.initializeSSE()
  }

  // If user just logged out (was logged in, now not)
  if (oldValue === true && newValue === false) {
    jobStore.disconnectSSE()

    // If currently on a protected route, redirect to login
    if (route.meta.requiresAuth) {
      router.push({
        path: '/auth/login',
        query: { redirect: route.fullPath }
      })
    }
  }
})
</script>

<style>
/* Note: Using Roboto's tabular-nums feature instead of Roboto Mono for better visual consistency */

/* make the entire Vuetify app a column flex of full viewport height */
html, body, #app {
  height: 100%;
  margin: 0;
}
.v-application--wrap {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

/* only <v-main> grows and scrolls internally */
.v-main {
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Make v-main content area fill available height */
.v-main__wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* RouterView wrapper to fill available space */
.router-view-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* footer always visible at bottom */
.v-footer {
  flex-shrink: 0;
}

/* keep your existing helpers */
ul.tooltip-list li {
  list-style-type: none;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
